import type { BvsCameraManifest } from '~/types/bvs';

export function useBvsCameraManifest(sku: Ref<string>) {
  const nuxtApp = useNuxtApp();
  const manifest = shallowRef<BvsCameraManifest>();
  const isLoading = ref(false);
  /*
   * The manifest mounts the simulation section between the product header and
   * the tabs. Resolving it while the PDP is still hydrating inserts nodes into a
   * tree the hydration walker is patching and aborts the following sections.
   */
  const isHydrated = ref(!nuxtApp.isHydrating);

  if (import.meta.client && !isHydrated.value) {
    nuxtApp.hooks.hookOnce('app:suspense:resolve', () => {
      isHydrated.value = true;
    });
  }

  watch(
    [sku, isHydrated],
    async ([currentSku, hydrated], _previous, onCleanup) => {
      manifest.value = undefined;
      if (!import.meta.client || !currentSku || !hydrated) {
        isLoading.value = false;
        return;
      }

      let cancelled = false;
      onCleanup(() => {
        cancelled = true;
      });

      isLoading.value = true;
      try {
        const response = await $fetch<BvsCameraManifest | null>(
          `/api/bvs/cameras/${encodeURIComponent(currentSku)}`
        );
        if (!cancelled) {
          // Cameras without renders answer 204 with an empty body.
          manifest.value = response ?? undefined;
        }
      } catch {
        if (!cancelled) {
          manifest.value = undefined;
        }
      } finally {
        if (!cancelled) {
          isLoading.value = false;
        }
      }
    },
    { immediate: true }
  );

  return {
    manifest,
    isLoading,
    isAvailable: computed(() => Boolean(manifest.value))
  };
}
